import { nomeDoMes } from "@bookdental/schemas";
import { reais } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import type { LinhaMontada, SituacaoDaLinha } from "./montar";

const MOTIVO: Record<Exclude<SituacaoDaLinha, "ok">, string> = {
  ignorada: "ignorada (total ou fora)",
  invalida: "sem data ou valor",
  mesFechado: "mês fechado",
  repetida: "já lançada",
  producao: "receita em mês com faturamento da produção",
};

type Props = { linhas: LinhaMontada[]; incluirProducao: boolean; onIncluirProducao: (v: boolean) => void };

/** Passo 4: o que entra por mês e o que fica de fora (e por quê), antes de gravar. */
export function PassoPrevia({ linhas, incluirProducao, onIncluirProducao }: Props) {
  const entra = (l: LinhaMontada) => l.situacao === "ok" || (incluirProducao && l.situacao === "producao");
  const porMes = new Map<string, { entradas: number; saidas: number; n: number }>();
  for (const l of linhas.filter(entra)) {
    const m = porMes.get(l.lida.competencia!) ?? { entradas: 0, saidas: 0, n: 0 };
    if (l.tipo === "ENTRADA") m.entradas += Math.abs(l.lida.valorCents!);
    else m.saidas += Math.abs(l.lida.valorCents!);
    m.n += 1;
    porMes.set(l.lida.competencia!, m);
  }
  const fora = linhas.filter((l) => !entra(l) && l.situacao !== "ignorada");
  const contagem = Object.entries(MOTIVO).map(([k, rotulo]) => ({ rotulo, n: linhas.filter((l) => l.situacao === k && !entra(l)).length })).filter((c) => c.n);
  const temProducao = linhas.some((l) => l.situacao === "producao");

  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-700 dark:text-foreground">
        <strong>{linhas.filter(entra).length}</strong> lançamentos vão entrar
        {contagem.length > 0 && <span className="text-zinc-500"> · fora: {contagem.map((c) => `${c.n} ${c.rotulo}`).join(", ")}</span>}
      </p>
      {temProducao && (
        <label className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
          <input type="checkbox" checked={incluirProducao} onChange={(e) => onIncluirProducao(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand-600" />
          Há receita de serviços em meses que já têm faturamento da produção no DentalBook: ela contaria duas vezes. Marque só se a planilha tiver receitas que o DentalBook não conhece.
        </label>
      )}
      <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:bg-muted/40 dark:text-muted-foreground">
              <th className="px-3 py-2">Mês</th>
              <th className="px-3 py-2 text-right">Lançamentos</th>
              <th className="px-3 py-2 text-right">Entradas</th>
              <th className="px-3 py-2 text-right">Saídas</th>
            </tr>
          </thead>
          <tbody>
            {[...porMes].sort(([a], [b]) => a.localeCompare(b)).map(([mes, v]) => (
              <tr key={mes} className="border-t border-zinc-100 dark:border-border/50">
                <td className="px-3 py-1.5">{nomeDoMes(mes)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{v.n}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700 dark:text-emerald-400">{reais(v.entradas)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-red-700 dark:text-red-400">{reais(v.saidas)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {fora.length > 0 && (
        <details className="text-xs text-zinc-600 dark:text-muted-foreground">
          <summary className="cursor-pointer font-medium">Ver as linhas que ficam de fora ({fora.length})</summary>
          <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto">
            {fora.slice(0, 200).map((l, i) => (
              <li key={i} className={cn("flex justify-between gap-3")}>
                <span className="truncate">
                  Linha {l.lida.linhaDaPlanilha}: {l.lida.descricao}
                </span>
                <span className="shrink-0">{MOTIVO[l.situacao as Exclude<SituacaoDaLinha, "ok">]}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
